import React, { useState, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { fetchAuth, fetchAndDownload } from '../../utils/api';
import ConfirmationModal from '../common/ConfirmationModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function LeadsView({ user, handleApiError, showNotification }) {
    const [leads, setLeads] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false); 
    const [chartData, setChartData] = useState(null);
    const fileInputRef = React.useRef(null);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [leadToDelete, setLeadToDelete] = useState(null);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('csvFile', file);

        try {
            showNotification('Importing leads... This may take a moment.', 'success');
            const res = await fetchAuth(`${API_URL}/api/leads/import`, {
                method: 'POST',
                body: formData,
                headers: { 'Content-Type': null }
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result.message);
            showNotification(result.message, 'success');
            fetchLeadsAndChartData();
        } catch (err) {
            handleApiError(err);
        } finally {
            event.target.value = null;
        }
    };

    const fetchLeadsAndChartData = useCallback(async () => {
        try {
            const [leadsRes, chartRes] = await Promise.all([
                fetchAuth(`${API_URL}/api/leads`),
                fetchAuth(`${API_URL}/api/leads/chart-data`)
            ]);
            
            if (leadsRes.ok) setLeads(await leadsRes.json());
            if (chartRes.ok) {
                const chartDataResponse = await chartRes.json();
                setChartData({
                    labels: chartDataResponse.labels,
                    datasets: [{
                        label: 'New Leads per Month',
                        data: chartDataResponse.data,
                        backgroundColor: 'rgba(99, 102, 241, 0.8)',
                        borderColor: 'rgba(99, 102, 241, 1)',
                        borderWidth: 1,
                    }]
                });
            }
        } catch (error) {
            handleApiError(error);
        }
    }, [handleApiError]);

    useEffect(() => {
        fetchLeadsAndChartData();
    }, [fetchLeadsAndChartData]);

    const handleSaveLead = async (newLead) => {
        if (!newLead.name) {
            showNotification('Lead name is required.', 'error');
            return;
        }
        try {
            const res = await fetchAuth(`${API_URL}/api/leads`, {
                method: 'POST',
                body: JSON.stringify(newLead)
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Failed to add lead');
            }
            showNotification('Lead added successfully!', 'success');
            setIsModalOpen(false);
            fetchLeadsAndChartData();
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleDeleteLead = async () => {
        if (!leadToDelete) return;
        try {
            const res = await fetchAuth(`${API_URL}/api/leads/${leadToDelete._id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error((await res.json()).message);
            showNotification('Lead deleted successfully!', 'success');
            fetchLeadsAndChartData();
        } catch (err) {
            handleApiError(err);
        } finally {
            setIsConfirmModalOpen(false);
            setLeadToDelete(null);
        }
    };

    const openConfirmModal = (lead) => {
        setLeadToDelete(lead);
        setIsConfirmModalOpen(true);
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { 
            legend: { display: false } 
        },
        scales: { 
            x: {
                grid: {
                    display: false
                }
            },
            y: { 
                beginAtZero: true,
                ticks: { 
                    precision: 0 
                },
                grid: {
                    display: false
                }
            } 
        }
    };

    const AddLeadModal = ({ onClose, onSave }) => {
        const [leadData, setLeadData] = useState({ name: '', phone: '', email: '', source: '', targetPrice: '', buyerName: '', buyerNumber: '' });
        const handleInputChange = (e) => {
            const { name, value } = e.target;
            setLeadData(prev => ({ ...prev, [name]: value }));
        };
        const handleSubmit = (e) => {
            e.preventDefault();
            onSave(leadData);
        };

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>Add New Lead</h3>
                    <form onSubmit={handleSubmit}>
                        <input type="text" name="name" placeholder="Lead Name" value={leadData.name} onChange={handleInputChange} required />
                        <input type="number" name="targetPrice" placeholder="Target Price (₹)" value={leadData.targetPrice} onChange={handleInputChange} />
                        <input type="text" name="buyerName" placeholder="Buyer's Name" value={leadData.buyerName} onChange={handleInputChange} />
                        <input type="text" name="buyerNumber" placeholder="Buyer's Number" value={leadData.buyerNumber} onChange={handleInputChange} />
                        <input type="text" name="phone" placeholder="Lead Contact Number" value={leadData.phone} onChange={handleInputChange} />
                        <input type="email" name="email" placeholder="Lead Email Address" value={leadData.email} onChange={handleInputChange} />
                        <input type="text" name="source" placeholder="Lead Source" value={leadData.source} onChange={handleInputChange} />
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button type="submit" className="confirm-btn">Save Lead</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className="page-container">
            {isModalOpen && <AddLeadModal onClose={() => setIsModalOpen(false)} onSave={handleSaveLead} />}
            {isConfirmModalOpen && (
                <ConfirmationModal 
                    onCancel={() => setIsConfirmModalOpen(false)}
                    onConfirm={handleDeleteLead}
                    customerName={leadToDelete?.name}
                />
            )}
            
           <div className="page-header">
                <h2 className="page-title">Leads Management</h2>
                <div className="header-actions">
                    <button className="btn-success" onClick={handleImportClick}>
                        <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM9.5 14c.83 0 1.5-.67 1.5-1.5S10.33 11 9.5 11s-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm5 0c.83 0 1.5-.67 1.5-1.5S15.33 11 14.5 11s-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm-5-3h5v-1h-5v1z"></path></svg>
                        Import Leads
                    </button>
                    <input type="file" ref={fileInputRef} onChange={handleFileImport} style={{ display: 'none' }} accept=".csv" />
                    <button className="btn-export" onClick={() => fetchAndDownload(`${API_URL}/api/leads/export`, 'leads.csv')}>
                        <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path></svg>
                        Export Leads
                    </button>
                    <button className="btn-primary" onClick={() => setIsModalOpen(true)}>
                        <svg viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path></svg>
                        Add Lead
                    </button>
                </div>
            </div>

            <div className="stat-card-container">
                 <div className="stat-card">
                    <div className="stat-icon" style={{backgroundColor: '#3b82f6'}}>
                         <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"></path></svg>
                    </div>
                    <div className="stat-info">
                        <p>Total Leads</p>
                        <span>{leads.length}</span>
                    </div>
                </div>
            </div>

            <div className="data-display" style={{marginTop: '24px'}}>
                <h3>Current Leads</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Phone</th>
                                <th>Email</th>
                                <th>Source</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map(lead => (
                                <tr key={lead._id}>
                                    <td>{lead.name}</td>
                                    <td>{lead.phone || 'N/A'}</td>
                                    <td>{lead.email || 'N/A'}</td>
                                    <td>{lead.source || 'N/A'}</td>
                                    <td><span className={`status-badge status-${lead.status.toLowerCase()}`}>{lead.status}</span></td>
                                    <td>
                                        <div className="table-actions">
                                            <button className="icon-btn delete" onClick={() => openConfirmModal(lead)}>
                                                <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="chart-container" style={{marginTop: '24px'}}>
                <h3>Monthly Leads Overview</h3>
                <div className="chart-wrapper">
                    {chartData ? (
                        <Bar options={chartOptions} data={chartData} />
                    ) : (
                        <div className="chart-placeholder">Loading chart data...</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LeadsView;
